/** @forge-header
 * @id          arbitrage
 * @role        Cross-venue arb detection | fee-adjusted spread between Kalshi and Polymarket
 * @pattern     pure-fn
 * @purity      none
 * @units       cents
 * @money-path  yes
 * @upstream    types:ConsolidatedGameBooks | pricing/fees:perContractFeeCents,takerRateForTicker
 * @downstream  market/index (barrel)
 * @exports     detectArbs:fn | takerFeeCents:fn | feeAdjustedBuyCents:fn | feeAdjustedSellCents:fn | ArbOpportunity:interface | ArbDirection:type
 * @test        arbitrage.test.ts (12 cases)
 *
 * @invariant   FEE_DELEGATION | fee formula delegates to perContractFeeCents (pricing.ts) | no local formula or constant duplication (T-006)
 * @invariant   PROFIT_FORMULA | profitPer1000 = (netSpreadCents / 100) * 1000 = dollars | /100 converts cents→dollars, *1000 scales to 1000 contracts
 * @invariant   FEE_LOOKUP | rates resolve per-ticker via hydra fee schedule | a ticker with no schedule yet returns null and the arb is skipped, never assumed fee-free
 */

import type { ConsolidatedGameBooks } from '@/types';
import { perContractFeeCents, takerRateForTicker } from '@/lib/pricing/fees';

export type ArbDirection = 'buy_kalshi_sell_poly' | 'buy_poly_sell_kalshi';

export interface ArbOpportunity {
  /** Game key for UI reference */
  gameKey: string;
  /** Team codes */
  awayCode: string;
  homeCode: string;
  /** Which team/side the arb is on */
  side: 'away' | 'home';
  /** Direction of the arb */
  direction: ArbDirection;
  /** Buy price in cents (fee-adjusted) */
  buyPriceCents: number;
  /** Sell price in cents (fee-adjusted) */
  sellPriceCents: number;
  /** Raw spread in cents before fees */
  rawSpreadCents: number;
  /** Net spread in cents after fees */
  netSpreadCents: number;
  /** Profit per 1000 contracts in dollars */
  profitPer1000: number;
  /** Buy venue */
  buyVenue: 'kalshi' | 'polymarket';
  /** Sell venue */
  sellVenue: 'kalshi' | 'polymarket';
  /** Available size (min of buy ask size and sell bid size) */
  availableSize: number;
  /** Timestamp of detection */
  detectedAt: number;
}

/**
 * Compute taker fee for a trade in cents at a specific Kalshi ticker.
 * Returns `null` if the per-series fee schedule isn't loaded yet — callers
 * must skip the arb opportunity rather than assume a default rate.
 *
 * Polymarket tickers (PM- prefix) resolve to 0.
 */
export function takerFeeCents(priceCents: number, ticker: string | null | undefined): number | null {
  const rate = takerRateForTicker(ticker);
  if (rate === null) return null;
  return perContractFeeCents(priceCents, rate);
}

/**
 * Fee-adjusted cost to BUY at a given ask price. Returns null if the
 * fee rate is unknown for the ticker.
 */
export function feeAdjustedBuyCents(askPriceCents: number, ticker: string | null | undefined): number | null {
  const fee = takerFeeCents(askPriceCents, ticker);
  if (fee === null) return null;
  return askPriceCents + fee;
}

/**
 * Fee-adjusted proceeds from SELLING at a given bid price. Returns null
 * if the fee rate is unknown for the ticker.
 */
export function feeAdjustedSellCents(
  bidPriceCents: number,
  ticker: string | null | undefined
): number | null {
  const fee = takerFeeCents(bidPriceCents, ticker);
  if (fee === null) return null;
  return bidPriceCents - fee;
}

/**
 * Detect arbitrage between two venues for a single game side.
 *
 * Arb exists when:
 *   fee_adjusted_sell(venue_A) > fee_adjusted_buy(venue_B)
 *   i.e., you can buy cheaper than you sell, even after fees.
 */
function detectSideArb(
  gameKey: string,
  awayCode: string,
  homeCode: string,
  side: 'away' | 'home',
  kalshiTicker: string | null,
  polyTicker: string | null,
  kalshiYesBestBid: number | null,
  kalshiYesBestAsk: number | null,
  kalshiYesBidSize: number,
  kalshiYesAskSize: number,
  polyYesBestBid: number | null,
  polyYesBestAsk: number | null,
  polyYesBidSize: number,
  polyYesAskSize: number,
  nowMs: number
): ArbOpportunity | null {
  const arbs: ArbOpportunity[] = [];

  // Direction 1: Buy Poly, Sell Kalshi
  if (polyYesBestAsk !== null && kalshiYesBestBid !== null) {
    const buyAdj = feeAdjustedBuyCents(polyYesBestAsk, polyTicker);
    const sellAdj = feeAdjustedSellCents(kalshiYesBestBid, kalshiTicker);
    if (buyAdj !== null && sellAdj !== null) {
      const netSpread = sellAdj - buyAdj;
      if (netSpread > 0) {
        arbs.push({
          gameKey,
          awayCode,
          homeCode,
          side,
          direction: 'buy_poly_sell_kalshi',
          buyPriceCents: polyYesBestAsk,
          sellPriceCents: kalshiYesBestBid,
          rawSpreadCents: kalshiYesBestBid - polyYesBestAsk,
          netSpreadCents: netSpread,
          profitPer1000: (netSpread / 100) * 1000,
          buyVenue: 'polymarket',
          sellVenue: 'kalshi',
          availableSize: Math.min(polyYesAskSize, kalshiYesBidSize),
          detectedAt: nowMs,
        });
      }
    }
  }

  // Direction 2: Buy Kalshi, Sell Poly
  if (kalshiYesBestAsk !== null && polyYesBestBid !== null) {
    const buyAdj = feeAdjustedBuyCents(kalshiYesBestAsk, kalshiTicker);
    const sellAdj = feeAdjustedSellCents(polyYesBestBid, polyTicker);
    if (buyAdj !== null && sellAdj !== null) {
      const netSpread = sellAdj - buyAdj;
      if (netSpread > 0) {
        arbs.push({
          gameKey,
          awayCode,
          homeCode,
          side,
          direction: 'buy_kalshi_sell_poly',
          buyPriceCents: kalshiYesBestAsk,
          sellPriceCents: polyYesBestBid,
          rawSpreadCents: polyYesBestBid - kalshiYesBestAsk,
          netSpreadCents: netSpread,
          profitPer1000: (netSpread / 100) * 1000,
          buyVenue: 'kalshi',
          sellVenue: 'polymarket',
          availableSize: Math.min(kalshiYesAskSize, polyYesBidSize),
          detectedAt: nowMs,
        });
      }
    }
  }

  // Return the best arb (highest net spread)
  if (arbs.length === 0) return null;
  return arbs.sort((a, b) => b.netSpreadCents - a.netSpreadCents)[0];
}

/** Helper: extract best bid/ask from BookLevel arrays */
function extractBestPrices(
  yesBids: { priceCents: number; size: number }[],
  noBids: { priceCents: number; size: number }[]
): {
  bestBidCents: number | null;
  bestAskCents: number | null;
  bestBidSize: number;
  bestAskSize: number;
} {
  const bestBid = yesBids.length > 0 ? yesBids[0] : null;
  const bestNoBid = noBids.length > 0 ? noBids[0] : null;
  return {
    bestBidCents: bestBid?.priceCents ?? null,
    // @ai-note: Standard binary market inversion: ask = 100 - NO bid.
    bestAskCents: bestNoBid ? 100 - bestNoBid.priceCents : null,
    bestBidSize: bestBid?.size ?? 0,
    bestAskSize: bestNoBid?.size ?? 0,
  };
}

/**
 * Scan all consolidated games for arbitrage opportunities.
 * Returns opportunities sorted by net spread (best first).
 */
export function detectArbs(games: ConsolidatedGameBooks[], nowMs?: number): ArbOpportunity[] {
  const now = nowMs ?? Date.now();
  const results: ArbOpportunity[] = [];

  for (const game of games) {
    if (!game.kalshi || !game.polymarket) continue;

    // Check away side
    const kalshiAway = extractBestPrices(game.kalshi.markets.away.yes, game.kalshi.markets.away.no);
    const polyAway = extractBestPrices(
      game.polymarket.markets.away.yes,
      game.polymarket.markets.away.no
    );

    const awayArb = detectSideArb(
      game.key,
      game.awayCode,
      game.homeCode,
      'away',
      game.kalshi.markets.away.marketTicker ?? null,
      game.polymarket.markets.away.marketTicker ?? null,
      kalshiAway.bestBidCents,
      kalshiAway.bestAskCents,
      kalshiAway.bestBidSize,
      kalshiAway.bestAskSize,
      polyAway.bestBidCents,
      polyAway.bestAskCents,
      polyAway.bestBidSize,
      polyAway.bestAskSize,
      now
    );
    if (awayArb) results.push(awayArb);

    // Check home side
    const kalshiHome = extractBestPrices(game.kalshi.markets.home.yes, game.kalshi.markets.home.no);
    const polyHome = extractBestPrices(
      game.polymarket.markets.home.yes,
      game.polymarket.markets.home.no
    );

    const homeArb = detectSideArb(
      game.key,
      game.awayCode,
      game.homeCode,
      'home',
      game.kalshi.markets.home.marketTicker ?? null,
      game.polymarket.markets.home.marketTicker ?? null,
      kalshiHome.bestBidCents,
      kalshiHome.bestAskCents,
      kalshiHome.bestBidSize,
      kalshiHome.bestAskSize,
      polyHome.bestBidCents,
      polyHome.bestAskCents,
      polyHome.bestBidSize,
      polyHome.bestAskSize,
      now
    );
    if (homeArb) results.push(homeArb);
  }

  return results.sort((a, b) => b.netSpreadCents - a.netSpreadCents);
}

/**
 * @ai-modification-checklist
 *
 * Before modifying this file, verify:
 *
 * 1. FEE MODEL — Delegates to perContractFeeCents + takerRateForTicker
 *    from pricing/fees. No local duplication (T-006 resolved).
 *
 * 2. FEE LOOKUP — Rates resolve per-ticker via hydra's fee schedule.
 *    A ticker with no entry in the cache returns null from takerFeeCents
 *    and the arb is skipped — never assumed fee-free.
 *
 * 3. PROFIT CALC — profitPer1000 = (netSpreadCents/100) * 1000 = dollars.
 *    The division converts cents→dollars before scaling.
 *
 * 4. BID↔ASK INVERSION — extractBestPrices: ask = 100 - NO bid. Standard
 *    binary market identity used throughout the codebase.
 *
 * 5. TEST FILE — arbitrage.test.ts (12 cases).
 *    Run: npm run dashboard:test -- arbitrage
 */
